import * as React from 'react';
import {connect} from 'react-redux';
import {IFamilyMember} from '@app-types/websocket/family.types';
import {WithStyles, withStyles,} from '@material-ui/core';
import TranslateNew from '@translate/TranslateNew';
import { IDayData } from '../calendar.types';
import { MonthviewStyle } from './monthview.style';
import Day from './Day';
import { ISchedule } from '@app-types/websocket/schedule.types';
import { IEvent, ILocalEvent } from '@app-types/websocket/event.types';
import { IDateRange } from '@containers/calendar/calendar.helpter';
import { addDays, getWeekNum, toIsoDate } from '@helpers/DateHelper';
import { HELP_TOPICS } from '@constants/suggestionTypes';
import * as UserActions from '@services/user/user.actions';
import { holidays as allHolidays } from '../holidays/holidays';
import { IExternalCalenderEvent } from '@app-types/api/externalCalendar.types';
import { schoolAgeFromFamily } from '@helpers/schoolHelper';

const tn: TranslateNew = TranslateNew.getInstance();

interface IProps extends WithStyles<typeof MonthviewStyle> {
  userId:number;
  localization: "se"|"en";
  size:"small"|"medium"|"large";  // should always be small
  focusDay:Date;
  selectedEditDays:IDateRange[];
  highlight:IDateRange[];
  family:IFamilyMember[];
  scheduleOne:ISchedule|undefined;
  scheduleTwo:ISchedule|undefined;
  scheduleThree:ISchedule|undefined;
  events:IEvent[];
  importedEvents:ILocalEvent[];
  isScrolling:boolean;
  isEditing:boolean;
  hideEvents?:boolean;
  setFocusDate: (year:number,month:number,date:number, position:"LEFT"|"RIGHT"|"NAN", isLongPress:boolean) => void;
  popMenu:(ref:HTMLDivElement|null) => void;
  showWeekNumber:boolean;
  dismissHelpNotice: (notice:string) => void;
  dismissedHelpLongclick:boolean;
  onlyWeeksOf?:number;
  holidays:{ [key:string]:{title:string, type:"HOLIDAY"|"SPECIAL_DAY"}; };
  externalEventsPerDay:{ [day: string]: IExternalCalenderEvent []} 
}

interface IState {
  someValue: number;
}

class WeekInSmallView extends React.Component<IProps, IState> {

  constructor(props: IProps) {
    super(props);

    this.state = {
      someValue: 1,
    };

  }

  public render() {
    const {classes, size, scheduleOne, scheduleTwo, scheduleThree, family, events, 
      isEditing, hideEvents, focusDay, localization} = this.props;
    
    const days: JSX.Element[] = [];

    const weekData:IDayData[] = [];

    let spinDate:Date = new Date(focusDay.getTime());
    while ((localization === "se" && spinDate.getDay() !== 1) || (localization === "en" && spinDate.getDay() !== 0)) {
      spinDate = addDays(spinDate, -1);
    }

    let i = 0;
    while (i < 7) {
      
      const startOfDay = new Date(spinDate.getTime());
      startOfDay.setHours(0);
      startOfDay.setMinutes(0);
      startOfDay.setSeconds(0);
      startOfDay.setMilliseconds(0);

      const endOfDay = new Date(spinDate.getTime());
      endOfDay.setHours(23);
      endOfDay.setMinutes(59);
      endOfDay.setSeconds(59);
      endOfDay.setMilliseconds(999);


      const data:IDayData = {
        year:spinDate.getFullYear(),
        month:spinDate.getMonth(),
        date:spinDate.getDate(),
        startTime:startOfDay.getTime(),
        endTime:endOfDay.getTime(),
      };

      weekData.push(data);
      spinDate = addDays(spinDate, 1);
      i++;
    }
    
    const weekNum:number = getWeekNum(new Date(((weekData[0].startTime + weekData[0].endTime + 1) / 2)));

    const schoolAge:number = schoolAgeFromFamily(this.props.family);

    let firstDay = true;
    for (const day of weekData) {
      
      let isSelectedForEdit:boolean = false;
      const midDay = ((day.startTime + day.endTime + 1) / 2);
      for (const range of this.props.selectedEditDays) {
        if (midDay > range.start && midDay < range.end ) {
          isSelectedForEdit = true;
          break;
        }
      }
      let highlight:boolean = false;
      for (const range of this.props.highlight) {
        if (midDay > range.start && midDay < range.end ) {
          highlight = true;
          break;
        }
      }
      const isFocus:boolean = (this.props.focusDay.getDate() === day.date &&
        this.props.focusDay.getMonth() === day.month &&
        this.props.focusDay.getFullYear() === day.year);

      days.push(<Day 
        holidays={this.props.holidays}
        userId={this.props.userId}
        isEditing={isEditing}
        key={day.date} 
        localization={this.props.localization} 
        size={size} 
        schoolAge={schoolAge}
        family={family}
        events={events}
        externalEvents={this.props.externalEventsPerDay[toIsoDate(new Date(midDay))]}
        data={day}
        importedEvents={this.props.importedEvents}
        scheduleOne={scheduleOne}
        scheduleTwo={scheduleTwo}
        scheduleThree={scheduleThree}
        isSelectedForEdit={isSelectedForEdit} 
        isFocus={isFocus}
        isHighlight={highlight}
        setFocusDate={this.props.setFocusDate} 
        popMenu={this.props.popMenu}
        showWeekNumber={this.props.showWeekNumber}
        onlyWeeksOf={this.props.onlyWeeksOf}
        needLeftBorder={firstDay}
        hasDismissedLongPress={this.props.dismissedHelpLongclick}
        dismissHelpNotice={this.props.dismissHelpNotice}
        hideEvents={hideEvents} />);

      firstDay = false;
    }

    return (
      <div className={classes.month} style={{minHeight:50}}>
        <div className={classes.week} style={{height:40, padding: '0px'}}>
          {this.props.showWeekNumber && <div className={classes.weekNumber}>{weekNum}</div>}
          {days}
        </div>
      </div>
    );
  }

}



function mapStateToProps(state: any) {
  const userId = state.user.session.data.id;
  const events = state.event.events;
  const country = (state.user.calendarSettings && state.user.calendarSettings.holidaysCountry )
    ?state.user.calendarSettings.holidaysCountry
    :undefined;
  const holidays = allHolidays.getHolidays(country);
  const dismissedHelpMessages = state.user.dismissedHelpNotices;
  let dismissedHelpLongclick = false;
  if (dismissedHelpMessages) {
    if (dismissedHelpMessages.includes(HELP_TOPICS.LONGCLICK)) {
      dismissedHelpLongclick = true;
    }
  }
  return {
    userId,
    family: state.family.family,
    events,
    holidays,
    dismissedHelpLongclick,
  };
}


const mapDispatchToProps = {
  dismissHelpNotice:UserActions.dismissHelpNotice,
  
};

export default connect(mapStateToProps,mapDispatchToProps)(withStyles(MonthviewStyle)(WeekInSmallView));



// WEBPACK FOOTER //
// ./src/components/calendar2/monthView/WeekInSmallView.tsx